import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { FcGoogle } from 'react-icons/fc';
import shareVideo from '../assets/share.mp4';
import logo from '../assets/logowhite.png';
import avatar from '../assets/default-avatar.png';
import { jwtDecode } from 'jwt-decode';
import { client } from '../client';

const Login = () => {
  const navigate = useNavigate();
  const [guestLoading, setGuestLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');

  // ✅ Check user status from Sanity
  const checkUserStatus = async (userId) => {
    const query = `*[_type == "user" && _id == "${userId}"][0]{status, adminReason}`;
    return await client.fetch(query);
  };

  // ✅ Google login response
  const responseGoogle = async (credentialResponse) => {
    const { credential } = credentialResponse;
    if (!credential) {
      alert('Login failed. Missing credential.');
      return;
    }

    const decoded = jwtDecode(credential);
    const { name, sub: googleId, picture: imageUrl } = decoded || {};

    const doc = {
      _id: googleId,
      _type: 'user',
      userName: name,
      image: imageUrl,
    };

    try {
      await client.createIfNotExists(doc);

      // 🔹 Check status before logging in
      const userStatus = await checkUserStatus(googleId);
      if (userStatus?.status === 'deleted' || userStatus?.status === 'suspended') {
        alert(
          `🚫 Your account is ${userStatus.status}.\nReason: ${userStatus.adminReason || 'No reason provided'
          }`
        );
        localStorage.removeItem('user');
        return;
      }

      localStorage.setItem('user', JSON.stringify(doc));
      navigate('/', { replace: true });
      window.location.reload();
    } catch (err) {
      alert('Google login failed. Try again.');
    }
  };

  // ✅ Guest login (unchanged)
  const handleGuestLogin = () => {
    setGuestLoading(true);

    const guestUser = {
      _id: `guest_${Date.now()}`,
      _type: 'user',
      userName: 'Guest User',
      image: avatar,
      guest: true,
    };

    localStorage.setItem('user', JSON.stringify(guestUser));
    setTimeout(() => {
      setGuestLoading(false);
      navigate('/', { replace: true });
      window.location.reload();
    }, 800);
  };

  // ✅ Email login with status check
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const res = await fetch('http://localhost:4000/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginForm.email,
          password: loginForm.password,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setLoginError(data.error || 'Login failed.');
        setLoginLoading(false);
        return;
      }

      // 🔹 Check status before logging in
      const userStatus = await checkUserStatus(data.user._id);
      if (userStatus?.status === 'deleted' || userStatus?.status === 'suspended') {
        alert(
          `🚫 Your account is ${userStatus.status}.\nReason: ${userStatus.adminReason || 'No reason provided'
          }`
        );
        localStorage.removeItem('user');
        setLoginLoading(false);
        return;
      }

      localStorage.setItem('user', JSON.stringify(data.user));
      setLoginLoading(false);
      navigate('/', { replace: true });
      window.location.reload();
    } catch (err) {
      setLoginError('Login failed. Try again.');
      setLoginLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMsg('');
    try {
      const res = await fetch('http://localhost:4000/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        setForgotMsg(data.error || 'Failed to send reset link.');
        return;
      }
      setForgotMsg('If this email exists, a reset link has been sent.');
    } catch {
      setForgotMsg('Failed to send reset link.');
    }
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_API_TOKEN}>
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-100 via-purple-400 to-pink-100 dark:from-gray-900 dark:via-slate-900 dark:to-blue-900 transition-colors duration-300">
        <div className="relative w-full max-w-md mx-auto rounded-3xl overflow-hidden shadow-2xl">
          <video
            src={shareVideo}
            type="video/mp4"
            loop
            controls={false}
            muted
            autoPlay
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="relative z-10 flex flex-col justify-center items-center min-h-[520px] px-6 py-10 bg-gradient-to-br from-blue-300 via-purple-600 to-pink-300 dark:bg-gray-900/80 backdrop-blur-xl">
            <img src={logo} width="170px" alt="logo" className="mb-8 drop-shadow-lg" />

            {!showForgot ? (
              <form
                className="w-full max-w-xs flex flex-col gap-4 mb-4"
                onSubmit={handleEmailLogin}
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={loginForm.email}
                  onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-blue-400"
                  autoComplete="email"
                  required
                />
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={loginForm.password}
                  onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-blue-400"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-3 rounded-lg shadow hover:from-blue-600 hover:to-purple-600 transition"
                >
                  {loginLoading ? 'Logging in...' : 'Login'}
                </button>
                {loginError && <div className="text-red-500 text-sm">{loginError}</div>}
                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  className="text-blue-200 dark:text-blue-400 text-sm hover:underline mt-2 self-end"
                >
                  Forgot Password?
                </button>
              </form>
            ) : (
              <form
                className="w-full max-w-xs flex flex-col gap-4 mb-4"
                onSubmit={handleForgotPassword}
              >
                <input
                  type="email"
                  name="forgotEmail"
                  placeholder="Enter your email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-blue-400"
                  required
                />
                <button
                  type="submit"
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-3 rounded-lg shadow hover:from-blue-600 hover:to-purple-600 transition"
                >
                  Send Reset Link
                </button>
                {forgotMsg && <div className="text-blue-200 dark:text-blue-400 text-sm">{forgotMsg}</div>}
                <button
                  type="button"
                  onClick={() => setShowForgot(false)}
                  className="text-green-400 dark:text-gray-400 text-sm hover:underline mt-2 self-end"
                >
                  Back to Login
                </button>
              </form>
            )}

            {/* Google Login */}
            <div className="w-full max-w-xs mb-4">
              <GoogleLogin
                onSuccess={responseGoogle}
                onError={() => alert('Google login failed')}
                useOneTap
                render={(renderProps) => (
                  <button
                    type="button"
                    className="w-full max-w-xs flex justify-center items-center p-3 rounded-lg cursor-pointer outline-none text-white font-semibold bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition shadow-lg"
                    onClick={renderProps.onClick}
                    disabled={renderProps.disabled}
                  >
                    <FcGoogle className="mr-4 text-2xl" /> Sign in with Google
                  </button>
                )}
              />
            </div>

            <div className="flex items-center w-full max-w-xs my-4">
              <div className="flex-grow border-t border-gray-300 dark:border-gray-700"></div>
              <span className="mx-3 text-gray-400 dark:text-gray-500 font-semibold">or</span>
              <div className="flex-grow border-t border-gray-300 dark:border-gray-700"></div>
            </div>

            {/* Guest Login */}
            <button
              onClick={handleGuestLogin}
              disabled={guestLoading}
              className="w-full max-w-xs bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-semibold transition"
            >
              {guestLoading && (
                <svg className="inline mr-2 w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
              )}
              {guestLoading ? 'Entering as Guest...' : 'Continue as Guest'}
            </button>

            <button
              onClick={() => navigate('/register')}
              className="mt-8 text-blue-100 dark:text-blue-200 font-semibold hover:underline transition"
            >
              Don't have an account? <span className="underline">Register</span>
            </button>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default Login;